import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  InputNumber,
  Button,
  message,
  Spin,
  Alert,
  Row,
  Col,
  Tabs,
  Grid,
  Space,
  Tooltip,
  Switch,
  Typography,
} from 'antd';
import axios from 'axios';
import RecipeMethodList from '../components/recipe/Method';
import IngredientSummary from '../components/recipe/IngredientSummary';
import MethodStepInlineCreator from '../components/recipe/MethodStepInlineCreator';
import RecipePhotosGrid from '../components/recipe/PhotosGrid';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import ChatbotWrapper from '../components/Chatbot';

const { useBreakpoint } = Grid;

const RecipeForm = () => {
  const { recipeId } = useParams();

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(Boolean(recipeId));
  const [error, setError] = useState(null);

  const [fetchedRecipe, setFetchedRecipe] = useState(null);
  const [ingredients, setIngredients] = useState(null);
  const [recipeMethodIngredients, setRecipeMethodIngredients] = useState([]);
  const [editingStep, setEditingStep] = useState(null);
  const [readonly, setReadonly] = useState(true);

  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');

  const [requiredportions, setrequiredportions] = useState(null);
  const [portions, setportions] = useState(null);
  const [mobileTab, setMobileTab] = useState('recipe');

  const MethodStepInlineCreatorRef = useRef(null);

  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();

  // ---------------- FETCHING ----------------

  const fetchPhotos = async (id) => {
    if (!id) return;
    setPhotosLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/recipe/photos?recipeId=${id}`,
        { withCredentials: true }
      );
      const fetched = Array.isArray(res.data) ? res.data : [];
      setPhotos(fetched);

      if (fetched.length > 0) {
        setBackgroundImageUrl(
          `${process.env.REACT_APP_BACKEND_URL}/recipe/photos/display/${fetched[0].id}`
        );
      } else {
        setBackgroundImageUrl('');
      }
    } catch {
      messageApi.error('Failed to load photos');
      setPhotos([]);
    } finally {
      setPhotosLoading(false);
    }
  };

  const fetchRecipe = async (suppressLoading = false) => {
    if (!recipeId) {
      if (!suppressLoading) setLoading(false);
      form.resetFields();
      setFetchedRecipe(null);
      setIngredients(null);
      setRecipeMethodIngredients([]);
      setPhotos([]);
      setBackgroundImageUrl('');
    
      return;
    }
    if (!suppressLoading) setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/recipe/${recipeId}/method`,
        { withCredentials: true }
      );
      setFetchedRecipe(response.data.recipe);
      setrequiredportions(response.data.recipe.requiredportions ?? response.data.recipe.portions);
      setportions(response.data.recipe.portions);
      setIngredients(response.data.ingredientSummary);
      setRecipeMethodIngredients(response.data.recipeMethodIngredients || []);
      await fetchPhotos(recipeId);
    } catch (err) {
      console.error(err);
      setError('Failed to load recipe data');
    } finally {
      if (!suppressLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  useEffect(() => {
    if (fetchedRecipe) {
      form.setFieldsValue(fetchedRecipe);
    } else {
      form.resetFields();
    }
  }, [fetchedRecipe, form]);

  useEffect(() => {
    // if creating a new recipe, put in edit mode
    setReadonly(Boolean(recipeId));
  }, [recipeId]);

  // --------------- SAVE RECIPE ----------------

  const onFinish = async (values) => {
    try {
      if (recipeId) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/recipes/${recipeId}`,
          values,
          { withCredentials: true }
        );
        messageApi.success('Recipe updated successfully');
        await fetchRecipe();
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/recipes`,
          values,
          { withCredentials: true }
        );
        const newId = response.data.id;
        navigate(`/ui/recipe/${newId}`, { replace: true });

        setFetchedRecipe(response.data);
        setIngredients(null);
        setRecipeMethodIngredients([]);
        setLoading(false);
        messageApi.success('Recipe created successfully');
      }
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to save recipe');
    }
  };


const handleChatRefresh = async (recipeId) => {
  console.log('🔄 Chatbot triggered recipe refresh:', recipeId);
  await fetchRecipe(true);  // suppressLoading: true
  messageApi.success('Recipe updated!');
};

  // --------------- METHOD STEPS ----------------

  const handleAddMethodStep = async (newItem) => {
    try {
      newItem.recipeMethod.order = recipeMethodIngredients.length + 1;
      const methodRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/recipes/method`,
        newItem,
        { withCredentials: true }
      );
      const newMethod = methodRes.data;
      setRecipeMethodIngredients((prev) =>
        prev ? [...prev, newMethod] : [newMethod]
      );
      await fetchRecipe(true);
      messageApi.success('Step added!');
    } catch (err) {
      console.error(err);
      messageApi.error('Failed to add step');
    }
  };

  const handleDeleteMethod = async (item) => {
    if (!item || !item.recipeMethod) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/recipes/method/${item.recipeMethod.id}`,
        { withCredentials: true }
      );
      setRecipeMethodIngredients((prev) =>
        prev.filter(
          (step) =>
            step.recipeMethod?.id !== item.recipeMethod.id &&
            step.id !== item.recipeMethod.id
        )
      );
      messageApi.success(item.recipeMethod.description + ' deleted');
      await fetchRecipe(true);
    } catch (err) {
      messageApi.error('Failed to delete step');
      console.error(err);
    }
  };

  const handleEditMethod = (item, idx) => {
    setEditingStep({ ...item, index: idx });
  };

  useEffect(() => {
    if (editingStep && MethodStepInlineCreatorRef.current) {
      setTimeout(() => {
        MethodStepInlineCreatorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  }, [editingStep]);

  const handleSaveEditedStep = async (edited) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/recipes/method/${edited.recipeMethod.id}`,
        edited,
        { withCredentials: true }
      );
      const updated = res.data;
      setRecipeMethodIngredients((prev) =>
        prev.map((step, idx) =>
          idx === editingStep.index ||
          step.recipeMethod.id === updated.recipeMethod.id
            ? updated
            : step
        )
      );
      setEditingStep(null);
      messageApi.success('Step updated!');
      await fetchRecipe(true);
    } catch (err) {
      messageApi.error('Failed to update step');
    }
  };

  // --------------- URL ACTIONS ----------------

  const handleCopyUrl = async () => {
    try {
      const url = fetchedRecipe?.sourceURL;
      if (url) {
        await navigator.clipboard.writeText(url);
        messageApi.success('URL copied to clipboard');
      }
    } catch {
      messageApi.error('Failed to copy URL');
    }
  };

  const handleOpenUrl = () => {
    const url = fetchedRecipe?.sourceURL;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const RecipeActionMenu = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <Switch
        checked={!readonly}
        checkedChildren="Edit Mode"
        unCheckedChildren="Read Only"
        onChange={(checked) => setReadonly(!checked)}
      />
    </div>
  );

const SourceURLInput = () => {
  const url = readonly ? fetchedRecipe?.sourceURL : form.getFieldValue('sourceURL');

  const buttons = url ? (
    <Space size={0} style={{ marginLeft: 8 }}>
      <Tooltip title="Copy URL">
        <Button icon={<CopyOutlined />} onClick={handleCopyUrl} type="text" />
      </Tooltip>
      <Tooltip title="Open URL in new tab">
        <Button icon={<LinkOutlined />} onClick={handleOpenUrl} type="text" />
      </Tooltip>
    </Space>
  ) : null;

if (readonly) {
  const display = isMobile ? getDomain(url) : url;
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere'
    }}>
      <Typography.Text style={{ 
        flex: 1, 
        whiteSpace: 'normal',  // Changed from nowrap
        overflow: 'hidden', 
        textOverflow: 'ellipsis',
        display: 'block'
      }}>
        {display || 'No source URL'}
      </Typography.Text>
      {buttons}
    </div>
  );
}



// Editable branch - BIND TO FORM FIELD
return (
  <Input.Group style={{ display: 'flex', width: '100%' }}>
    <Form.Item name="sourceURL" noStyle>
      <Input 
        placeholder="Enter source URL" 
        style={{ flex: 1 }}
      />
    </Form.Item>
    {buttons}
  </Input.Group>
);

};


const getDomain = (url) => {
  if (!url) return '';
  try {
    const hostname = new URL(url).hostname;
    return hostname.replace(/^www\./i, '');
  } catch {
    // if user saved something without protocol, fall back to original
    return url;
  }
};



  // --------------- Portions Required ----------------

const updateRequiredPortions = async (newValue) => {

  setrequiredportions(newValue);
  
  if (recipeId) {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/recipes/${recipeId}/requiredportions`, {
        requiredportions: newValue
      }, { withCredentials: true });
      
      // Refresh recipe data
      await fetchRecipe(true);
    } catch (err) {
      console.error('Failed to update portions');
    }
  }
};


  // --------------- SHARED PANELS ----------------

  if (loading) return <Spin tip="Loading recipe..." />;
  if (error) return <Alert type="error" message={error} />;

  

  const IngredientsPanel = recipeId ? (
    <IngredientSummary ingredientSummary={ingredients} readonly={readonly} portions={portions} requiredportions={requiredportions}/>
  ) : null;

  const PhotosPanel = recipeId ? (
    <RecipePhotosGrid
      recipeId={recipeId}
      readonly={readonly}
      photos={photos}
      setPhotos={setPhotos}
      loading={photosLoading}
    />
  ) : null;

  const MethodPanel =
    recipeId && (
      <div style={{ maxWidth: 700, margin: '24px auto' }}>
        <RecipeMethodList
          recipeMethodIngredients={recipeMethodIngredients}
          onDeleteMethod={handleDeleteMethod}
          onEditMethod={handleEditMethod}
          readonly={readonly}
          requiredportions={requiredportions}
          portions={portions}
        />
      </div>
    );

  // Reusable info card (description + URL + portions + calories)
  const InfoCard = () => (
    <Form.Item label={null} style={{ marginBottom: 0 }}>
      <div
        style={{
          padding: '16px 18px',
          borderRadius: 12,
          border: '1px solid #f0f0f0',
          background: '#fcfcfc',
          display: 'grid',
          rowGap: 16,
        }}
      >
        {/* Description */}
        <div>
          <div
            style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}
          >
            Description
          </div>
          {readonly ? (
            <Typography.Text style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
              {fetchedRecipe?.description || '-'}
            </Typography.Text>
          ) : (
            <Form.Item name="description" style={{ margin: 0 }}>
              <Input.TextArea
                rows={3}
                placeholder="Enter description"
              />
            </Form.Item>
          )}
        </div>

        {/* Source URL */}
        <div>
          <div
            style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}
          >
            Source URL
          </div>
          <Form.Item
            name="sourceURL"
            style={{ margin: 0 }}
            rules={[
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <SourceURLInput />
          </Form.Item>
        </div>

        {/* Divider */}
        <div
          style={{
            height: 1,
            background: 'rgba(0,0,0,0.06)',
            margin: '4px 0',
          }}
        />

        {/* Portions + calories */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 24,
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 120 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                marginBottom: 4,
              }}
            >
              Portions
            </div>
            {readonly ? (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 8,
      padding: '4px 8px',
      border: '1px solid rgba(0,0,0,0.1)',
      borderRadius: 6,
      background: 'rgba(0,0,0,0.02)'
    }}>
      <Button
        size="small"
        type="text"
        icon="-"
        onClick={() => {
          const newValue = ((requiredportions ?? portions) || 0) - 0.5;
          // Update via API or local state as needed
          updateRequiredPortions(Math.max(0.5, newValue));
        }}
        style={{ fontSize: 16, width: 24, height: 24 }}
      />
      <Typography.Text style={{ fontSize: 15, minWidth: 40, textAlign: 'center' }}>
        {requiredportions.toFixed(1) ?? portions}
      </Typography.Text>
      <Button
        size="small"
        type="text"
        icon="+"
        onClick={() => {
          const newValue = ((requiredportions ?? portions) || 1) + 0.5;
          updateRequiredPortions(newValue);
        }}
        style={{ fontSize: 16, width: 24, height: 24 }}
      />
    </div>
  ): (
              <Form.Item
                name="portions"
                style={{ margin: 0 }}
                rules={[
                  {
                    type: 'number',
                    min: 1,
                    message: 'Portions must be at least 1',
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  placeholder="Number of portions corresponding to quantities"
                />
              </Form.Item>
            )}
          </div>

          {fetchedRecipe?.caloriesPerPortion != null && (
            <div style={{ textAlign: 'right', minWidth: 140 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  marginBottom: 4,
                  color: 'rgba(0,0,0,0.65)',
                }}
              >
                Calories per portion
              </div>
              <div
                style={{
                  display: 'inline-block',
                  padding: '3px 12px',
                  borderRadius: 999,
                  background: 'rgba(0,0,0,0.04)',
                  fontSize: 13,
                }}
              >
                {fetchedRecipe.caloriesPerPortion} cals
              </div>
            </div>
          )}
        </div>
      </div>
    </Form.Item>
  );

  // --------------- RENDER ----------------

  return (
    <>
      {/* Background hero */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '480px',
          backgroundImage: backgroundImageUrl
            ? `url(${backgroundImageUrl})`
            : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: '2px',
          boxShadow: '0 4px 32px rgba(0,0,0,0.17)',
        }}
      >
        { (
          <div
            style={{
              position: 'absolute',
              bottom: '32px',
              left: '32px',
              color: 'white',
              background: 'rgba(0, 0, 0, 0.45)',
              padding: '16px 24px',
              borderRadius: '8px',
              fontSize: '4rem',
              fontWeight: 'bold',
              letterSpacing: '2px',
            }}
          >
            {fetchedRecipe?.name}
          </div>

          
        )}
      </div>

      {/* Main content */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          minHeight: '100vh',
        }}
      >
        {contextHolder}
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
                {/* ── Sticky tab bar ── */}
                <div style={{
                  position: 'sticky',
                  top: 60,
                  zIndex: 200,
                  backgroundColor: '#fff',
                  borderBottom: '1px solid #f0f0f0',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                  display: 'flex',
                  padding: '0 16px',
                }}>
                  {[
                    { key: 'recipe', label: 'Recipe' },
                    { key: 'ingredients', label: 'Ingredients' },
                    { key: 'method', label: 'Method' },
                    { key: 'assistant', label: 'Assistant' },
                  ].map(t => (
                    <button
                      key={t.key}
                      onClick={() => setMobileTab(t.key)}
                      style={{
                        flex: 1,
                        padding: '12px 0',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: mobileTab === t.key ? 600 : 400,
                        color: mobileTab === t.key ? '#1677ff' : '#595959',
                        borderBottom: mobileTab === t.key ? '2px solid #1677ff' : '2px solid transparent',
                        transition: 'all 0.2s',
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* ── Tab content ── */}
                {mobileTab === 'assistant' ? (
                  // Assistant: fixed height, chatbot scrolls internally, page does NOT scroll
                  <div style={{
                    height: 'calc(100vh - 60px - 45px)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <ChatbotWrapper recipe={fetchedRecipe} embedded={true} isOpen={true} onRecipeRefresh={handleChatRefresh} />
                  </div>
                ) : (
                  // All other tabs: normal page scroll
                  <div style={{ padding: '0 16px' }}>
                    {mobileTab === 'recipe' && (
                      <>
                        <RecipeActionMenu />
                        <Form
                          form={form}
                          layout="vertical"
                          onFinish={onFinish}
                          style={{
                            maxWidth: 600,
                            margin: 'auto',
                            marginTop: 24,
                            background: 'rgba(255,255,255,0.96)',
                            borderRadius: 12,
                            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                            padding: 16,
                          }}
                        >
                          {!readonly && (
                            <Form.Item
                              name="name"
                              label={<span style={{ fontWeight: 600 }}>Recipe name</span>}
                              rules={[{ required: true, message: 'Please enter the recipe name' }]}
                            >
                              <Input placeholder="Enter recipe name" />
                            </Form.Item>
                          )}
                          <Form.Item label={null} style={{ marginBottom: 0 }}>
                            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #f0f0f0', background: '#fcfcfc', display: 'grid', rowGap: 12 }}>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Description</div>
                                {readonly ? (
                                  <Typography.Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{fetchedRecipe?.description || '-'}</Typography.Text>
                                ) : (
                                  <Form.Item name="description" style={{ margin: 0 }}><Input.TextArea rows={3} placeholder="Enter description" /></Form.Item>
                                )}
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Source URL</div>
                                <Form.Item name="sourceURL" style={{ margin: 0 }} rules={[{ type: 'url', message: 'Please enter a valid URL' }]}>
                                  <SourceURLInput />
                                </Form.Item>
                              </div>
                              <div style={{ height: 1, background: 'rgba(0,0,0,0.06)', margin: '2px 0' }} />
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div style={{ display: 'grid', rowGap: 8 }}>
                                  <div>
                                    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 2 }}>Portions</div>
                                    {readonly ? (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 6, background: 'rgba(0,0,0,0.02)' }}>
                                        <Button size="small" type="text" icon="-" onClick={() => updateRequiredPortions(Math.max(0.5, ((requiredportions ?? portions) || 0) - 0.5))} style={{ fontSize: 16, width: 24, height: 24 }} />
                                        <Typography.Text style={{ fontSize: 15, minWidth: 40, textAlign: 'center' }}>{requiredportions?.toFixed(1) ?? portions}</Typography.Text>
                                        <Button size="small" type="text" icon="+" onClick={() => updateRequiredPortions(((requiredportions ?? portions) || 1) + 0.5)} style={{ fontSize: 16, width: 24, height: 24 }} />
                                      </div>
                                    ) : (
                                      <Form.Item name="portions" style={{ margin: 0 }} rules={[{ type: 'number', min: 1, message: 'Portions must be at least 1' }]}>
                                        <InputNumber min={1} style={{ width: '100%' }} placeholder="Number of portions" />
                                      </Form.Item>
                                    )}
                                  </div>
                                  {fetchedRecipe?.caloriesPerPortion != null && (
                                    <div>
                                      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, color: 'rgba(0,0,0,0.65)' }}>Calories per portion</div>
                                      <div style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, background: 'rgba(0,0,0,0.04)', fontSize: 12 }}>{fetchedRecipe.caloriesPerPortion} cals</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Form.Item>
                          {!readonly && (
                            <Form.Item style={{ marginTop: 16 }}>
                              <Button type="primary" htmlType="submit" block>Save Recipe</Button>
                            </Form.Item>
                          )}
                        </Form>
                      </>
                    )}

                    {mobileTab === 'ingredients' && (
                      <Space direction="vertical" size={24} style={{ width: '100%', marginTop: 16 }}>
                        {IngredientsPanel}
                        {PhotosPanel}
                      </Space>
                    )}

                    {mobileTab === 'method' && (
                      <>
                        {MethodPanel}
                        {recipeId && !readonly && (
                          <div style={{ maxWidth: 700, margin: '24px auto' }} ref={MethodStepInlineCreatorRef}>
                            <MethodStepInlineCreator
                              key={editingStep ? 'edit' + editingStep.recipeMethod.id : 'new'}
                              recipeId={recipeId}
                              onNewStep={editingStep ? handleSaveEditedStep : handleAddMethodStep}
                              initialStep={editingStep}
                              onCancelEdit={() => setEditingStep(null)}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
        ) : (
          <Row
            gutter={[8, 0]}
            style={{
              maxWidth: 1600,
              margin: '24px auto 0',
              width: '100%'
            }}
          >
            <Col xs={24} md={12}>
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                style={{
                  maxWidth: 700,
                  margin: 'auto',
                  background: 'rgba(255,255,255,0.925)',
                  borderRadius: 12,
                  boxShadow:
                    '0 4px 32px rgba(0,0,0,0.06)',
                  padding: '24px',
                  border: '2px solid #e8e5e5ff',
                }}
              >
                {!readonly && (
                  <Form.Item
                    name="name"
                    label={
                      <span style={{ fontWeight: 600 }}>
                        Recipe Name
                      </span>
                    }
                    rules={[
                      {
                        required: true,
                        message:
                          'Please enter the recipe name',
                      },
                    ]}
                  >
                    <Input placeholder="Enter recipe name" />
                  </Form.Item>
                )}

                <InfoCard />

                {!readonly && (
                  <Form.Item style={{ marginTop: 16 }}>
                    <Button type="primary" htmlType="submit">
                      Save Recipe
                    </Button>
                  </Form.Item>
                )}
              </Form>

              {MethodPanel}

              {recipeId && !readonly && (
                <div
                  style={{
                    maxWidth: 700,
                    margin: '24px auto',
                  }}
                  ref={MethodStepInlineCreatorRef}
                >
                  <MethodStepInlineCreator
                    key={
                      editingStep
                        ? 'edit' + editingStep.recipeMethod.id
                        : 'new'
                    }
                    recipeId={recipeId}
                    onNewStep={
                      editingStep
                        ? handleSaveEditedStep
                        : handleAddMethodStep
                    }
                    initialStep={editingStep}
                    onCancelEdit={() => setEditingStep(null)}
                  />
                </div>
              )}
            </Col>

            <Col xs={24} md={12}>
              <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                {/* Ingredients + Photos column */}
                <div style={{ flex: '0 0 260px' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <RecipeActionMenu />
                  </div>
                  {IngredientsPanel}
                  <div style={{ marginTop: 24 }}>
                    {PhotosPanel}
                  </div>
                </div>
                {/* Chatbot column */}
                <div style={{ flex: 1, minWidth: 0, maxWidth: '100%', position: 'sticky', top: 60, height: 'calc(100vh - 60px)' }}>
                  <ChatbotWrapper recipe={fetchedRecipe} embedded={true} isOpen={true} onRecipeRefresh={handleChatRefresh} />
                </div>
              </div>
            </Col>
            
          </Row>
        )}
      </div>
    </>
  );
};

export default RecipeForm;